import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { voicePlansApi } from '@/api/voicePlans'
import { apiErrorMessage, isOfflineError } from '@/lib/errors'
import { useNetworkStatus } from '@/composables/useNetworkStatus'
import type { VoicePlan } from '@/types/models'

// Global (not view-scoped) so an upload started from RecordView keeps
// reporting its progress/failure via the app-wide status indicator even
// after the user navigates to another screen and RecordView unmounts.
export const useVoicePlanUploadStore = defineStore('voicePlanUpload', () => {
  const { isOnline } = useNetworkStatus()

  const submitting = ref(false)
  const submitError = ref<string | null>(null)
  const canRetry = ref(false)
  // Set when the current submitError was caused by the device being
  // offline, rather than e.g. a server-side rejection. Read by the
  // isOnline watcher so it only auto-retries connectivity failures — a
  // genuine server error still requires the manual Retry button.
  const wasOffline = ref(false)
  const pendingProjectId = ref<string | null>(null)
  // Tracks whether the composable (RecordView) is mounted. The store watcher
  // only auto-retries if this is false, so the composable watcher can handle
  // retry + routing when mounted.
  const isRecordViewActive = ref(false)

  // Kept around so a failed upload can be retried from the status indicator
  // on any screen without re-recording — retry() re-sends this same blob.
  let pendingBlob: Blob | null = null
  let pendingFilename: string | null = null

  // Bumped by dismiss() so a request already in flight at the moment of a
  // dismiss (e.g. the user logs out mid-upload) can't resurrect stale
  // error/retry state after the fact — its catch/finally becomes a no-op
  // once it's no longer the current request. Otherwise a failure landing
  // just after logout would surface as a retryable "Upload failed" banner
  // for whichever session/user is active on this tab by the time it
  // resolves, offering to retry with the previous session's audio blob.
  let requestVersion = 0

  // Used to abort in-flight upload requests when dismiss() is called, so
  // the server doesn't process an upload that the user discarded. The abort
  // controller is created fresh for each upload attempt and discarded once
  // the request settles (whether it completes or aborts).
  let currentAbortController: AbortController | null = null

  async function upload(projectId: string, blob: Blob, filename: string): Promise<VoicePlan> {
    const thisRequestVersion = ++requestVersion
    // Snapshot at the start of the request, not just checked fresh in the
    // catch below — the device can flip back online while the request is
    // still in flight, and by the time the catch runs a live check would
    // read online and misclassify what was actually a connectivity failure
    // as a server error, silently skipping the auto-retry it should trigger.
    const startedOffline = !isOnline.value
    pendingProjectId.value = projectId
    pendingBlob = blob
    pendingFilename = filename
    submitError.value = null
    canRetry.value = false
    submitting.value = true

    currentAbortController = new AbortController()
    const signal = currentAbortController.signal

    try {
      const voicePlan = await voicePlansApi.upload(projectId, blob, filename, signal)
      if (thisRequestVersion === requestVersion) {
        pendingProjectId.value = null
        pendingBlob = null
        pendingFilename = null
        wasOffline.value = false
      }
      return voicePlan
    } catch (e: unknown) {
      if (thisRequestVersion === requestVersion) {
        wasOffline.value = startedOffline || isOfflineError(e)
        submitError.value = apiErrorMessage(e, 'Upload failed. Please try again.')
        canRetry.value = true
      }
      throw e
    } finally {
      if (thisRequestVersion === requestVersion) {
        submitting.value = false
        currentAbortController = null
      }
    }
  }

  // Re-sends the blob from the last failed upload() call. Returns null if
  // there's nothing pending (e.g. called with no prior failure) or an
  // upload is already in flight.
  async function retry(): Promise<VoicePlan | null> {
    if (submitting.value || !pendingProjectId.value || !pendingBlob || !pendingFilename) return null
    return upload(pendingProjectId.value, pendingBlob, pendingFilename)
  }

  // Called when the user starts a fresh recording after a failed upload
  // (so a stale pending blob isn't retried in place of the new take), and
  // when the session ends (see useAuthStore.reset()) so upload state never
  // crosses a logout/login boundary on a shared device. Also disowns any
  // request still in flight via requestVersion, and clears submitting
  // directly since that in-flight request's own finally is now a no-op.
  // Lets the composable (RecordView) tell the store whether it's mounted,
  // without reaching in and setting the ref directly.
  function setRecordViewActive(active: boolean): void {
    isRecordViewActive.value = active
  }

  function dismiss(): void {
    requestVersion++
    currentAbortController?.abort()
    currentAbortController = null
    pendingProjectId.value = null
    pendingBlob = null
    pendingFilename = null
    submitError.value = null
    canRetry.value = false
    submitting.value = false
    wasOffline.value = false
  }

  // Auto-retries a connectivity-caused failure as soon as the device comes
  // back online, but only if the composable (RecordView) is not active. When
  // RecordView is mounted, its composable watcher handles the retry + routing.
  // When the user has navigated away to another screen, this store watcher
  // handles the auto-retry so they don't miss it.
  watch(isOnline, (online) => {
    if (!isRecordViewActive.value && online && wasOffline.value && canRetry.value && !submitting.value) {
      // store already records submitError/canRetry on failure; nothing else to do here.
      retry().catch(() => {})
    }
  })

  return {
    submitting,
    submitError,
    canRetry,
    wasOffline,
    pendingProjectId,
    isRecordViewActive,
    upload,
    retry,
    setRecordViewActive,
    dismiss,
  }
})
